import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'reactstrap';
import classNames from 'classnames';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';

import TableFilterComponent from '../../../../shared/components/form/TableFilterComponent';
import TableHeaderLabel from '../../../../shared/components/table/TableHeaderLabel';
import CommCopyHistoryBlockedPrompt from '../../../../shared/components/table/CommCopyHistoryBlockedPrompt';
import { getStaffDoctorsByFacility } from '../../../../redux/actions/staffActions';
import { handleRequest } from '../../../../redux/services/APIService';
import {
  INQUIRY_MESSAGE_STATUS,
  INQUIRY_STATUSES,
} from '../../../../config/app.config';
import { SortingField } from '../../../common/SortingCell';
import {
  getDefaultSortOrder,
  getSortFieldName,
} from '../../Common/SortBy/utils';
import { useGetFacilityServices } from '../../../App/customize/hooks/useGetFacilityServices';
import { buildMessageHistoryTranscript } from '../../../../utils/buildMessageHistoryTranscript';
import { InquiryStatusDropdown } from './InquiryStatusDropdown';
import {
  useInquiryModalContext,
  tab as inquiryTab,
} from '../utils/InquiryModalContext';

const COPIED_BADGE_DURATION_MS = 1500;

export function InquiriesTable({
  heads,
  rows,
  handleFilter,
  handleSort,
  sortField,
  sortOrder,
  stickyTop,
  onStatusChange,
  emptyStateMessage = 'No Inquiries to display',
}) {
  const dispatch = useDispatch();
  const [copyingInquiryId, setCopyingInquiryId] = useState(null);
  const [copiedInquiryId, setCopiedInquiryId] = useState(null);
  const [copyBlockedInquiryId, setCopyBlockedInquiryId] = useState(null);
  const copyBadgeTimeoutRef = useRef(null);

  const {
    openInquiryModal,
    handleSetInquiryData,
    setIsMessageModalOpen,
  } = useInquiryModalContext();
  const doctors = useSelector(
    (state) => state.staff.staffDoctorsByFacility || [],
  );
  const facilityId = useSelector((state) => state.user?.profile?.facility?._id);
  const { data: services } = useGetFacilityServices();
  const isBoardingOnly = !!services?.isBoardingOnly;

  useEffect(() => {
    if (facilityId) {
      dispatch(getStaffDoctorsByFacility());
    }
  }, [dispatch, facilityId]);

  useEffect(() => {
    return () => {
      if (copyBadgeTimeoutRef.current) {
        clearTimeout(copyBadgeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!copyBlockedInquiryId) {
      return undefined;
    }

  const dismissBlockedPrompt = () => {
    setCopyBlockedInquiryId(null);
  };

  document.addEventListener('mousedown', dismissBlockedPrompt);
  return () => {
    document.removeEventListener('mousedown', dismissBlockedPrompt);
  };
}, [copyBlockedInquiryId]);

const uniqueTopics = React.useMemo(() => {
    const topicSet = new Set();
    rows.forEach((row) => {
      if (row.topicTitle) topicSet.add(row.topicTitle);
    });
    return Array.from(topicSet).sort();
  }, [rows]);

  const handleStatusFilter = (key, list) => {
    const filterField = key === 'doctorFullName' ? 'doctorId' : key;
    handleFilter(filterField, list);
  };

  const handleCopyHistory = async (event, row) => {
    event.preventDefault();
    event.stopPropagation();

    if (copyingInquiryId) {
      return;
    }

    setCopyBlockedInquiryId(null);
    setCopyingInquiryId(row.id);

    try {
      const response = await handleRequest(
        'get',
        `contact-requests/${row.id}/messages`,
        true,
      );
      const messages = response.data || [];

      const text = buildMessageHistoryTranscript({
        requestType: 'Inquiry',
        header: {
          parent: row.parentFullName,
          pet: row.petName,
          doctor: isBoardingOnly ? '' : row.doctorFullName,
          topic: row.topicTitle,
          submitted: row.dateTime || `${row.date || ''} ${row.time || ''}`.trim(),
        },
        messages,
      });

      try {
        await navigator.clipboard.writeText(text);
      } catch (clipboardError) {
        console.error(clipboardError);
        setCopyBlockedInquiryId(row.id);
        setCopyingInquiryId(null);
        return;
      }

      if (copyBadgeTimeoutRef.current) {
        clearTimeout(copyBadgeTimeoutRef.current);
      }

      setCopiedInquiryId(row.id);
      setCopyingInquiryId(null);

      copyBadgeTimeoutRef.current = setTimeout(() => {
        setCopiedInquiryId(null);
        copyBadgeTimeoutRef.current = null;
      }, COPIED_BADGE_DURATION_MS);
    } catch (error) {
      console.error(error);
      setCopyingInquiryId(null);
    }
  };

  const hasDoctor = heads.some((head) => head && head.key === 'doctorFullName');
  const hasParent = heads.some(
    (head) => head && head.key === 'parentFullName',
  );
  const tableClassName = classNames(
    'custom-table',
    'loyal-table',
    'fixed-layout',
    {
      'inquiry-table--doctor': hasDoctor,
      'inquiry-table--no-parent': !hasParent,
    },
  );

  const renderRowValues = (row) => {
    const renderMessageStatusColumn = (head, value) => {
      let dotClass = 'none';
      let columnText = '\u2013';

      switch (value) {
        case INQUIRY_MESSAGE_STATUS.NEW_INQUIRY:
          dotClass = 'green';
          columnText = value;
          break;
        case INQUIRY_MESSAGE_STATUS.AWAIT_REPLY:
          dotClass = 'yellow';
          columnText = value;
          break;
        case INQUIRY_MESSAGE_STATUS.NEW_REPLY:
          dotClass = 'blue';
          columnText = value;
          break;
        case INQUIRY_MESSAGE_STATUS.STAFF_REPLIED:
          dotClass = 'purple';
          columnText = value;
          break;
        default:
          dotClass = 'none';
          columnText = '\u2013';
          break;
      }

      return (
        <td key={head.key}>
          <div className="message-status text-capitalize">
            <span className={classNames('dot', dotClass)}></span>
            <span>{columnText}</span>
          </div>
        </td>
      );
    };

    const renderInquiryStatusColumn = (head, value) => (
      <td key={head.key} onClick={(e) => e.stopPropagation()}>
        <InquiryStatusDropdown
          currentStatus={value}
          onStatusChange={(newStatus) => onStatusChange(row.id, newStatus)}
          className="inquiry-status-dropdown"
        />
      </td>
    );

    const renderAttachmentsColumn = (head) => {
      const isButtonVisible = !!row.documents?.length || !!row.photos?.length;

      return (
        <td
          key={head.key}
          className="custom-no-over-flow-td"
          onClick={(e) => {
            isButtonVisible && e.stopPropagation();
          }}
          style={{ textAlign: 'center' }}
        >
          {isButtonVisible && (
            <a
              style={{ padding: 0 }}
              className="view-tab-button full-width"
              onClick={() => {
                openInquiryModal(inquiryTab.ATTACHMENTS);
                handleSetInquiryData(row);
              }}
            >
              View
            </a>
          )}
        </td>
      );
    };

    const renderInternalCommentsColumn = (head) => {
      const isButtonVisible = !!row.staffInternalComments?.length;

      return (
        <td
          key={head.key}
          className="custom-no-over-flow-td"
          onClick={(e) => {
            isButtonVisible && e.stopPropagation();
          }}
          style={{ textAlign: 'center' }}
        >
          {isButtonVisible && (
            <a
              style={{ padding: 0 }}
              className="view-tab-button full-width"
              onClick={() => {
                openInquiryModal(inquiryTab.INTERNAL_COMMENTS);
                handleSetInquiryData(row);
              }}
            >
              View
            </a>
          )}
        </td>
      );
    };

    const renderMessageColumn = (head) => (
      <td key={head.key} onClick={(e) => e.stopPropagation()}>
        {!isRowClosed(row) && (
          <button
            type="button"
            className="nav-link-style message-button"
            onClick={() => {
              handleSetInquiryData(row);
              setIsMessageModalOpen(true);
            }}
          >
            Message
          </button>
        )}
      </td>
    );

    const renderCopyHistoryColumn = (head) => {
      const isCopying = copyingInquiryId === row.id;
      const showCopiedBadge = copiedInquiryId === row.id;
      const showBlockedPrompt = copyBlockedInquiryId === row.id;

      return (
        <td
          key={head.key}
          className={classNames('custom-no-over-flow-td', head.className)}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="comm-copy-history-wrap">
            <button
              type="button"
              className="comm-copy-history-btn"
              title="Copy history"
              disabled={isCopying}
              onClick={(event) => handleCopyHistory(event, row)}
            >
              <ContentCopyIcon size={16} aria-hidden />
            </button>
          </span>
          {showBlockedPrompt ? (
            <CommCopyHistoryBlockedPrompt recordLabel="Inquiry" />
          ) : null}
          {showCopiedBadge ? (
            <span className="comm-copy-history-copied-badge">Copied!</span>
          ) : null}
        </td>
      );
    };

    const defaultColumn = (head, value, capitalizeClass) => (
      <td
        key={head.key}
        title={value}
        className={classNames(
          'custom-no-over-flow-td',
          capitalizeClass,
          head.className,
        )}
      >
        {value}
      </td>
    );

    return heads.map((head) => {
      const value = row[head.key];
      const capitalizeClass = head.capitalize ? 'text-capitalize' : '';

      if (head.key === 'copyHistory') {
        return renderCopyHistoryColumn(head);
      }

      if (head.key === 'message') {
        return renderMessageColumn(head);
      }

      if (head.key === 'messageStatus') {
        return renderMessageStatusColumn(head, value);
      }

      if (head.key === 'inquiryStatus') {
        return renderInquiryStatusColumn(head, value);
      }

      if (head.key === 'attachments') {
        return renderAttachmentsColumn(head);
      }

      if (head.key === 'internalComments') {
        return renderInternalCommentsColumn(head);
      }

      return defaultColumn(head, value, capitalizeClass);
    });
  };

  const renderTableHeaders = () => {
    const renderSortableHeader = (head) => {
      const sortKey = getSortFieldName(head.key);
      const defaultOrder = getDefaultSortOrder(sortKey);

      return (
        <th key={head.key} className={head.className ?? ''}>
          {sortField === sortKey ? (
            <SortingField
              icon={sortOrder === 1 ? 'triangle-up' : 'triangle-down'}
              name={head.name}
              onSort={() => handleSort({ order: sortOrder === 1 ? -1 : 1 })}
            />
          ) : (
            <SortingField
              name={head.name}
              icon="diamond-narrow"
              onSort={() => handleSort({ field: sortKey, order: defaultOrder })}
            />
          )}
        </th>
      );
    };

    const renderFilterableHeader = (head) => (
      <th key={head.key} className={head.className ?? ''}>
        <TableFilterComponent
          allFilters={head.filterValue}
          action={(list) => {
            handleStatusFilter(head.key, list);
          }}
          label={head.name}
        />
      </th>
    );

    const renderIconHeader = (head) => (
      <th key={head.key} className={head.className ?? ''}>
        <img src={head.imgSrc} alt={head.imgAlt} title={head.name} />
      </th>
    );

    const renderDefaultHeader = (head) => (
      <th key={head.key} className={head.className ?? ''}>
        <TableHeaderLabel
          id={`table-header-info-${head.key}`}
          label={head.name}
          tooltip={head.headerTooltip}
        />
      </th>
    );

    const renderHeader = (head) => {
      if (!head) return null;

      if (head.sortable) {
        return renderSortableHeader(head);
      } else if (head.filterable) {
        if (head.key === 'doctorFullName') {
          if (doctors.length === 0) return renderDefaultHeader(head);

          const filterValues = doctors.map(({ _id, name }) => ({
            [_id]: `Dr. ${name}`,
          }));
          filterValues.push({ NA: 'N/A' });
          head.filterValue = filterValues;
        }

        if (head.key === 'topicTitle') {
          if (uniqueTopics.length === 0) return renderDefaultHeader(head);
          head.filterValue = uniqueTopics.map((topic) => ({ [topic]: topic }));
        }

        return renderFilterableHeader(head);
      } else if (head.imgSrc) {
        return renderIconHeader(head);
      } else {
        return renderDefaultHeader(head);
      }
    };

    return (
      <tr className="header-tr" style={stickyTop ? { top: stickyTop } : {}}>
        {heads.map((head) => renderHeader(head))}
      </tr>
    );
  };

  const isRowClosed = (row) =>
    row.inquiryStatus === INQUIRY_STATUSES.CLOSED ||
    row.inquiryStatus === INQUIRY_STATUSES.CLOSED_AUTO;

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="inquiry-table-scroll">
          <Table striped className={tableClassName}>
            <thead>{renderTableHeaders()}</thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  className={classNames('body-tr inquiry-row', {
                    closed: isRowClosed(row),
                  })}
                  key={index}
                  onClick={() => {
                    openInquiryModal();
                    handleSetInquiryData(row);
                  }}
                >
                  {renderRowValues(row)}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {rows.length === 0 ? (
          <div className="no-data-message">{emptyStateMessage}</div>
        ) : null}
      </div>
    </div>
  );
}
